import React, { useState, useEffect } from 'react';
import { useTheme, Grid, Paper, Box, Typography, Container, Fab, Tooltip, CircularProgress,
        Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, 
        ChevronRight as ChevronRightIcon, CameraAlt as CameraIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { TreeView } from '@mui/x-tree-view';
import { TreeItem, TreeItemProps, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import { TransitionProps } from '@mui/material/transitions';
import Collapse from '@mui/material/Collapse';
import { alpha, styled } from '@mui/material/styles';
import { useSpring, animated } from '@react-spring/web';
import { useS3Manager } from '../../hooks/useS3Manager';
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

// 
// *** TODO ***
// 
// bronze/silver accordion templates - one size doesn't fit all
// close all, expand all
// Search box

interface ISelectedItem {
  key: string,
  fileTypes: Array<string>,   // used to categorize files
  items: Array<string>
}

export default function DataExplorerView() {

  const theme = useTheme();  
  const { isS3initialized, getObjectsForBucket, downloadObject } = useS3Manager({ bucketName: process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET! }); 
  const [isRefreshRequired, setIsRefreshRequired] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allItems, setAllItems] = useState<Array<string>>([]);
  const [filteredItems, setFilteredItems] = useState<Array<string>>([]);
  const [selectedItem, setSelectedItem] = useState<ISelectedItem | null>(null);
  const [expanded, setExpanded] = React.useState<string | false>('panel0');

  useEffect(() => {

    if (!isS3initialized || !isRefreshRequired) return;

    const getItems = async () => {

      // Async call
      const prefix = `${process.env.REACT_APP_AWS_S3_DATA_LAKE_ROOT_PATH!}`
      const items = await getObjectsForBucket(process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET!, prefix);

      // Set props
      setAllItems(allItems);
      setFilteredItems(filterItems(items));
      setIsRefreshRequired(false);
      setIsLoading(false);

      // Log counts
      console.log(`DataExplorerView.useEffect :: getting s3 items :: total count: ${items.length}`)
    };

    setIsLoading(true);
    getItems();    

  }, [isS3initialized, isRefreshRequired]);

  const handleRefreshButtonClick = () => {
    setIsRefreshRequired(true);
  };

  const handleTreeItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, path: string): void => {
    
    const parts = path.split('/');
    const level = parts.length;

    // Snapshots are level 3, set selected snapshot (and render details)
    if (level === 3) {      
      const snapshotFiles = filteredItems.filter((item) => item.includes(path) );
      console.log(`handleTreeItemClick :: item selected :: path: ${path}, level: ${level}, file count: ${snapshotFiles.length}`);
      setSelectedItem({
        key: path,
        fileTypes: getFileTypes(snapshotFiles),
        items: snapshotFiles 
      });
      setExpanded('panel0');
    } else {
      setSelectedItem(null);
      setExpanded(false);
    }
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleFileDownloadClick = async (path: string) => {
    setIsLoading(true);
    const fullPath = `${process.env.REACT_APP_AWS_S3_DATA_LAKE_ROOT_PATH}/${path}`;
    console.log(`handleFileDownloadClick :: path: ${fullPath}`);
    await downloadObject(fullPath);
    setIsLoading(false);
  };

  const SnapshotIcon = () => {
    return (<CameraIcon sx={{ fontSize: 'small', color: theme.palette.primary.main }}/>);
  };

  const buildTree = (items: string[], basePath?: string): React.ReactNode[] => {
    
    const tree: Record<string, string[]> = {};

    items.forEach((item) => {

      const parts = item.split('/');
      const level = parts.length;
      const currentLevel = parts.shift(); // Remove the first part

      if (currentLevel) {
        if (!tree[currentLevel]) {
          tree[currentLevel] = [];
        }

        // Leaf node will be at snapshot level
        if (level > 0 && !currentLevel.includes('snapshot=')) {
          const nestedPath = parts.join('/');
          tree[currentLevel].push(nestedPath);
        }
      }
    });

    return Object.keys(tree).map((key, index) => {
      const fullPath = basePath ? `${basePath}/${key}` : key;
      const children = tree[key];
      return (
        <TreeItem 
          key={index} 
          nodeId={fullPath} 
          label={key}
          onClick={(e) => handleTreeItemClick(e, fullPath)}
          sx={{
            color: theme.palette.text.primary,
            fontSize: '1rem',
            py: .4
          }}>
          {buildTree(children, fullPath)}
        </TreeItem>
      );
    });
  };

  const tableCellStyle = {
    py: .5,
    my: 0,
    borderBottom: `solid 1px ${theme.palette.grey[300]}`,
  };

  return (
    <Grid item xs={12} sx={{ position: 'relative', height: '100%', }}>

      {/* Loading overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
          }}>
          <CircularProgress />
        </div>
      )}

      {/* File tree and details split pane */}
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', }}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom>
          Data Lake Explorer
        </Typography>
        <Paper component='div' sx={{ width: '100%', height: '100%', m: 0, mt: 2, p: 0 }}>
          <Allotment vertical={false} defaultSizes={[1, 3]}>
            <Allotment.Pane minSize={200}>

              <TreeView                
                aria-label="customized"
                defaultExpanded={['1']}
                defaultCollapseIcon={<MinusSquare />}
                defaultExpandIcon={<PlusSquare />}  
                defaultEndIcon={<SnapshotIcon />}                
                sx={{ 
                  overflowX: 'hidden', 
                  overflowY: 'auto',
                  maxHeight: '100%',
                  m: 2 
                }}>
                {buildTree(filteredItems)}
              </TreeView>

            </Allotment.Pane>
            <Allotment.Pane snap>  

              <Grid 
                container 
                spacing={1} 
                direction="column"
                justifyContent="flex-start"
                sx={{ 
                  height: '100%', overflowY: 'hidden', 
                  p: 0, pt: 1, mb: 0, pb: 0,                    
                  background: theme.palette.grey[100]}}>

                  {selectedItem && selectedItem.fileTypes.map((category, index) => (
                    <Accordion 
                      key={`${index}-${category}`}
                      expanded={expanded === `panel${index}`} 
                      onChange={handleAccordionChange(`panel${index}`)}
                      sx={{ 
                        width: '100%', overflowX: 'hidden',                          
                        py: 0, my: 0                          
                      }}>

                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        sx={{  }}>
                        <Typography
                          variant='h6'
                          color={theme.palette.text.secondary}
                          sx={{ mx: 2 }}>
                          {getAccordionGroupLabel(parsePath(selectedItem.key, 0), category)}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          overflowY: 'auto',
                          maxHeight: '50vh'
                        }}>
                        <TableContainer>
                          <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                              <TableRow>
                                <TableCell>Label</TableCell>
                                <TableCell>File</TableCell>
                                <TableCell>Download</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedItem && selectedItem.items.filter(path => path.endsWith(category)).map((path, index) => (
                                <TableRow 
                                  key={path}
                                  sx={{ '&:last-child td': { borderBottom: 0 }}}>
                                  <TableCell sx={tableCellStyle} >{parsePath(path, -2)}</TableCell>
                                  <TableCell sx={tableCellStyle}>{parsePath(path, -1)}</TableCell>
                                  <TableCell sx={tableCellStyle}>    
                                    <IconButton 
                                      sx={{ color: theme.palette.primary.main }}
                                      onClick={() => handleFileDownloadClick(path)}>
                                      <FileDownloadIcon />
                                    </IconButton></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}

              </Grid>                
            </Allotment.Pane>
          </Allotment>

          <Fab color="primary" aria-label="add" 
            sx={{ position: 'absolute', bottom: '50px', right: '65px' }}
            onClick={() => handleRefreshButtonClick()}>
            <RefreshIcon />
          </Fab>  

        </Paper>
      </Paper>
    </Grid>
  );
}

const MinusSquare = (props: SvgIconProps) => {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
    </SvgIcon>
  );
};

const PlusSquare = (props: SvgIconProps) => {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
    </SvgIcon>
  );
};

const CloseSquare = (props: SvgIconProps) => {
  return (
    <SvgIcon
      className="close"
      fontSize="inherit"
      style={{ width: 14, height: 14 }}
      {...props}
    >
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M17.485 17.512q-.281.281-.682.281t-.696-.268l-4.12-4.147-4.12 4.147q-.294.268-.696.268t-.682-.281-.281-.682.294-.669l4.12-4.147-4.12-4.147q-.294-.268-.294-.669t.281-.682.682-.281.696 .268l4.12 4.147 4.12-4.147q.294-.268.696-.268t.682.281 .281.669-.294.682l-4.12 4.147 4.12 4.147q.294.268 .294.669t-.281.682zM22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0z" />
    </SvgIcon>
  );
};

const TransitionComponent = (props: TransitionProps) => {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
};

/* eslint-disable react/display-name */
const CustomTreeItem = React.forwardRef(
  (props: TreeItemProps, ref: React.Ref<HTMLLIElement>) => (
    <TreeItem {...props} TransitionComponent={TransitionComponent} ref={ref} />
  ),
);

const StyledTreeItem = styled(CustomTreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.iconContainer}`]: {
    '& .close': {
      opacity: 0.3,
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

const getFileTypes = (items: Array<string>): Array<string> => {
  return Array.from(
    new Set(
      items
        .map(item => item.match(/\.\w+$/)) // Extract file extensions
        .filter(Boolean) // Remove null values
        .map(match => match![0]) // Convert RegExpMatchArray to string
    )
  );
};

const filterItems = (items: Array<string>) : Array<string> => {
  const prefixToRemove = 'data-lake/data/';
  const filteredItems = items    
    .map((item) => {
      if (item.startsWith(prefixToRemove)) {
        return item.substring(prefixToRemove.length);
      }
      return item;
    })
    .filter((item) => item.includes('snapshot=') && /\.\w+$/.test(item))  
    .sort((a, b) => {
      const order = { bronze: 1, silver: 2, gold: 3 } as Record<string, number>;
      const prefixA = a.split('/')[0].toLowerCase();
      const prefixB = b.split('/')[0].toLowerCase();
      return order[prefixA] - order[prefixB];
    });

  return filteredItems;
};

const parsePath = (path: string, index: number): string => {
  const parts = path.split('/');
  if (index >= parts.length || index < -parts.length) return '';
  if (index < 0) return parts[parts.length + index];
  return parts[index];
};

const getAccordionGroupLabel = (stage: string, fileType: string): string => {
  const stageStr = `${stage.charAt(0).toUpperCase()}${stage.slice(1)}`;
  return `${stageStr} ${fileType} files`;
};

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));



  // border: `solid 1px ${theme.palette.grey[300]}`
  // defaultEndIcon={<CloseSquare />}


{/* <Grid item key={item} xs={12} sm={6} md={4} lg={3}>
<Card variant="outlined" sx={{ height: '175px'}}>
  <CardContent>
    <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
      Bronze
    </Typography>
    <Typography variant="h5" component="div">
      {item}
    </Typography> <Typography sx={{ mb: 1.5 }} color="text.secondary">
      subtitle
    </Typography>
    <Typography variant="body2">
      {item}
    </Typography>
  </CardContent>
  <CardActions>
    <IconButton sx={{ color: theme.palette.primary.main }}>
      <FileDownloadIcon />
    </IconButton>
    <Button size="small">Learn More</Button>
  </CardActions>
</Card>
</Grid> */}


{/* {selectedItem && selectedItem.items.filter(path => path.endsWith(category)).map((path, index) => (
                            <Tooltip
                              key={path} 
                              title={parsePath(path, -1)}>
                              <Typography
                                sx={{ 
                                  mx: 2,
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%',
                                }}>
                                {parsePath(path, -1)}
                              </Typography>
                            </Tooltip>
                          ))} */}







                  {/* <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography>Expanded by default</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
            malesuada lacus ex, sit amet blandit leo lobortis eget.
          </Typography>
        </AccordionDetails>
      </Accordion> */}
                  
                  {/* {selectedItem && 
                    <Container component='div'>
                      <Typography 
                        variant="h6" color="text.secondary"
                        sx={{ fontSize: '0.7rem', mb:1,  }}>
                        Key: {selectedItem.key}, File Types: {selectedItem.fileTypes.join(', ')}
                      </Typography>
                    </Container>
                  } */}
                  
                  
                  {/* {selectedItem && selectedItem.items.map((item) => (
                    <Typography 
                      key={item}
                      variant="h6" color="text.secondary"
                      sx={{ fontSize: '0.7rem',mb:1 }} >
                      {item}
                    </Typography>
                  ))} */}




                  
                    {/* Accordion item per file type */}
                    {/* {selectedItem && selectedItem.fileTypes.map((category, index) => (
                      <Accordion 
                        key={`${index}-${category}`}
                        defaultExpanded={index === 0}
                        sx={{ 
                          width: '100%',
                          pt: 2
                          }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                          sx={{  }}>
                          <Typography
                            variant='h6'
                            color={theme.palette.text.secondary}
                            sx={{ mx: 2 }}>
                            {getAccordionGroupLabel(parsePath(selectedItem.key, 0), category)}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Label</TableCell>
                                    <TableCell>File</TableCell>
                                    <TableCell>Download</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {selectedItem && selectedItem.items.filter(path => path.endsWith(category)).map((path, index) => (
                                    <TableRow key={path}>
                                      <TableCell component="th" scope="row">{parsePath(path, -2)}</TableCell>
                                      <TableCell>{parsePath(path, -1)}</TableCell>
                                      <TableCell>    
                                        <IconButton sx={{ color: theme.palette.primary.main }}>
                                          <FileDownloadIcon />
                                        </IconButton></TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))} */}